import React, { useState } from "react";
import Avatar from "components/avatar";
import Icon from "components/icon";
import Typography from "components/typography";
import { useAppTranslation } from "locale/useAppTranslation";
import { ExtendedDriverDetailRes } from "_interfaces/driver.interfaces";
import { dateFormatter } from "_helper/timeFormatter";

interface DriverDetailCardProps {
    detail: ExtendedDriverDetailRes;
}

interface InfoFieldProps {
    label: string;
    value?: string | null;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <Typography variant="bodySmall" tone="secondary">
            {label}
        </Typography>
        <Typography variant="body">
            {value || "—"}
        </Typography>
    </div>
);

const DriverDetailCard: React.FC<DriverDetailCardProps> = ({ detail }) => {
    const { t } = useAppTranslation("driver");
    const [isExpanded, setIsExpanded] = useState(true);

    const statusLower = detail.status?.toLowerCase();
    const statusBadgeClass =
        statusLower === "available" || statusLower === "active"
            ? "bg-green-100 text-green-700"
            : statusLower === "assigned"
            ? "bg-blue-100 text-blue-700"
            : "bg-gray-100 text-gray-700";

    const driverPhoto =
        "driver_picture_url" in detail &&
            typeof detail.driver_picture_url === "string"
            ? detail.driver_picture_url
            : undefined;

    return (
        <section className="rounded-2xl border border-border bg-white shadow-sm">
            {/* Top Row: Avatar + Name + Status */}
            <div className="flex items-start justify-between p-6 pb-0">
                <div className="flex items-center gap-4">
                    <Avatar size="xl" name={detail.full_name} {...(driverPhoto ? { src: driverPhoto } : {})} />
                    <div className="flex flex-col gap-1">
                        <Typography variant="heading4">
                            {detail.full_name || "—"}
                        </Typography>
                        <Typography variant="bodySmall" tone="secondary">
                            {detail.ktp_number || "—"} {detail.vendor_name ? `• ${detail.vendor_name}` : ""}
                        </Typography>
                        <div className="mt-1 flex items-center gap-1.5">
                            <Icon name="Star" size={14} className="text-yellow-400" />
                            <Typography variant="bodySmall" tone="secondary">
                                {detail.star_rating ?? "—"}
                            </Typography>
                        </div>
                    </div>
                </div>

                <div
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusBadgeClass}`}
                >
                    {detail.status || "—"}
                </div>
            </div>

            {/* Main Info Grid */}
            <div className="grid grid-cols-5 gap-6 border-b border-border px-6 py-5">
                <InfoField
                    label={t("label-driver-code") || "Driver Code"}
                    value={detail.driver_code}
                />
                <InfoField
                    label={t("label-license-type")}
                    value={detail.license_type}
                />
                <InfoField
                    label={t("label-license-expiry-date") || "License Expire Date"}
                    value={dateFormatter(detail.license_expiry_date)}
                />
                <InfoField
                    label={t("label-phone-number") || "Phone Number"}
                    value={detail.phone_number}
                />
                <InfoField
                    label={t("label-join-date") || "Join Date"}
                    value={dateFormatter(detail.join_date)}
                />
            </div>

            {/* Collapsible Sections */}
            <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                    }`}
            >
                {/* Driver Address Section */}
                <div className="border-b border-border px-6 py-5">
                    <div className="mb-4 flex items-center gap-2">
                        <Icon name="location" size={18} className="text-text-secondary" />
                        <Typography variant="heading6">
                            {t("section-driver-address") || "Driver Address"}
                        </Typography>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <InfoField
                            label={t("label-domicile-address")}
                            value={detail.domicile_address}
                        />
                        <InfoField
                            label={t("label-citizen-address")}
                            value={detail.citizen_address}
                        />
                    </div>
                </div>

                {/* Emergency Contact Section */}
                <div className="border-b border-border px-6 py-5">
                    <div className="mb-4 flex items-center gap-2">
                        <Icon name="call" size={18} className="text-text-secondary" />
                        <Typography variant="heading6">
                            {t("section-emergency-contact") || "Emergency Contact"}
                        </Typography>
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                        <InfoField
                            label={t("label-emergency-name") || "Name"}
                            value={detail.emergency_contact_name}
                        />
                        <InfoField
                            label={t("label-emergency-phone") || "Phone Number"}
                            value={detail.emergency_contact_phone}
                        />
                        <InfoField
                            label={t("label-emergency-relation") || "Relation"}
                            value={detail.emergency_contact_relation}
                        />
                    </div>
                </div>

                {/* Financial Information Section */}
                <div className="px-6 py-5">
                    <div className="mb-4 flex items-center gap-2">
                        <Icon name="wallet" size={18} className="text-text-secondary" />
                        <Typography variant="heading6">
                            {t("section-financial-information") || "Financial Information"}
                        </Typography>
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                        <InfoField
                            label={t("label-bank-name")}
                            value={detail.bank_name}
                        />
                        <InfoField
                            label={t("label-bank-account")}
                            value={detail.bank_account_number}
                        />
                        <InfoField
                            label={t("label-account-holder")}
                            value={detail.bank_account_holder}
                        />
                    </div>
                </div>
            </div>

            {/* Expand/Collapse Toggle */}
            <div className="border-t border-border">
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex w-full items-center justify-center gap-2 py-3 text-text-secondary transition-colors hover:bg-greyScale-95"
                    aria-label={isExpanded ? "Collapse details" : "Expand details"}
                >
                    <Icon
                        name={isExpanded ? "arrow-up" : "arrow-down"}
                        size={16}
                        className="text-text-secondary transition-transform duration-200"
                    />
                </button>
            </div>
        </section>
    );
};

export default DriverDetailCard;
